import {
  IStarredMessageData,
  UnStarException,
  Unsubscribe,
} from "@home-health-notify/domain";
import { Box } from "@mui/material";
import {
  ApiErrorHandler,
  OrganizationContext,
  useScrollLock,
  useTranslation,
} from "hhn-commons";
import { useCallback, useContext, useEffect, useState } from "react";
import StarredMessageHeader from "./StarredMessageHeader";
import { StarredMessageList } from "./StarredMessageList";
import { UnstarAllModal } from "./UnstarAllModal";
import { useSnackbar } from "notistack";

const StarredMessages = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedOrgUser } = useContext(OrganizationContext);
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });

  const [starredMessagesData, setStarredMessagesData] =
    useState<IStarredMessageData>({
      data: [],
      hasNext: true,
      limit: 0,
      total: 0,
    });
  const [isStarredMessagesLoading, setIsStarredMessagesLoading] =
    useState<boolean>(false);
  const [isUnstarAllModalOpen, setIsUnstarAllModalOpen] =
    useState<boolean>(false);

  const { concurrencySafe } = useScrollLock();

  const fetchStarredMessages = useCallback(async () => {
    await concurrencySafe(async () => {
      if (starredMessagesData.hasNext && selectedOrgUser) {
        setIsStarredMessagesLoading(true);
        setStarredMessagesData(
          await selectedOrgUser.starredMessage.fetchStarredMessages(),
        );
        setIsStarredMessagesLoading(false);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgUser?.starredMessage, starredMessagesData.hasNext]);

  useEffect(() => {
    const removeListeners: Unsubscribe[] = [];
    if (selectedOrgUser) {
      const removeStarMessageListener =
        selectedOrgUser.starredMessage.starMessageListener(
          async (starredMessageData: IStarredMessageData) => {
            setStarredMessagesData(starredMessageData);
          },
        );
      const removeUnstarMessageListener =
        selectedOrgUser.starredMessage.unstarMessageListener(
          async (starredMessageData: IStarredMessageData) => {
            setStarredMessagesData(starredMessageData);
          },
        );
      const removeAddAcknowledgementListener =
        selectedOrgUser.starredMessage.addAcknowledgementListener(
          async (starredMessageData: IStarredMessageData) => {
            setStarredMessagesData(starredMessageData);
          },
        );
      const removeUnstarAllMessagesListener =
        selectedOrgUser.starredMessage.unstarAllMessagesListener(async () => {
          setStarredMessagesData(
            selectedOrgUser.starredMessage.getStarredMessages(),
          );
        });
      removeListeners.push(
        removeStarMessageListener,
        removeUnstarMessageListener,
        removeAddAcknowledgementListener,
        removeUnstarAllMessagesListener,
      );
    }

    return () => {
      removeListeners.forEach(removeListener => removeListener());
    };
  }, [selectedOrgUser, starredMessagesData.hasNext]);

  useEffect(() => {
    fetchStarredMessages();
  }, [fetchStarredMessages]);

  const closeUnstarAllModal = () => {
    setIsUnstarAllModalOpen(false);
  };

  const openUnstarAllModal = () => {
    setIsUnstarAllModalOpen(true);
  };

  const unstarAll = async () => {
    try {
      await selectedOrgUser?.starredMessage.unstarAllMessages();
      enqueueSnackbar(tNs("message.unStarAllSuccess"), {
        autoHideDuration: 3000,
      });
    } catch (e) {
      if (e instanceof UnStarException) {
        enqueueSnackbar(tNs("message.unStarFailed"), {
          autoHideDuration: 3000,
        });
      } else {
        ApiErrorHandler(e, enqueueSnackbar);
      }
    }
  };

  const showUnstarAllButton =
    (starredMessagesData &&
      starredMessagesData.total &&
      starredMessagesData.total > 0) ||
    false;

  return (
    <Box>
      <StarredMessageHeader
        onUnstarAllClick={openUnstarAllModal}
        showUnstarAll={showUnstarAllButton}
      />
      <StarredMessageList
        starredMessageTypeData={starredMessagesData.data}
        onScroll={fetchStarredMessages}
        isDataLoading={isStarredMessagesLoading}
      />
      <UnstarAllModal
        isOpen={isUnstarAllModalOpen}
        onClose={closeUnstarAllModal}
        unstarAll={unstarAll}
      />
    </Box>
  );
};

export default StarredMessages;
